import 'core-js/stable'
import 'regenerator-runtime/runtime'
import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { i18n } from 'element-react'
import english from './en'

import { actions as AuthActions } from '../store/slices/AuthSlice'
import { userFeatureFlags } from '../repositories/UsersRepository'

import { Provider } from 'react-redux'
import store from '../store/store'

import ScrollToTop from '../helpers/ScrollToTop'
import VersionCheck from '../helpers/VersionCheck'
import Routes from '../routes/Routes'
import BrowserCheck from './BrowserCheck'
import { setupExpiredSessionInterceptor } from '../utils/sessionInterceptor'
import { monitorOnlineOffline } from '../utils/networkStatus'
import { FetchStateProvider } from '../context/FetchStateContext'
import { getGAUserConfig } from '../utils/sendGAAnalyticsEvent'
// import ErrorBoundary from './ErrorBoundary/ErrorBoundary'
// import ErrorModal from './ErrorModal/ErrorModal'
// import { reloadWithoutCache } from '../utils/reloadWithoutCache'
import { notifyErrorModalShown, REASONS } from '../utils/notifyErrorModalShown'

import { memoize } from 'lodash'

if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register('/service-worker.js')
            .then(registration => {
                console.log(
                    'Service Worker registered with scope:',
                    registration.scope,
                )
            })
            .catch(error => {
                console.log('Service Worker registration failed:', error)
            })
    })
}

const initializeGA = () => {
    // Matches the production-only gating used by the rest of the GA helpers
    // in sendGAAnalyticsEvent.js, so dev/qa traffic never reaches the GA property.
    if (process.env.MIX_APP_ENV !== 'production') return

    const GA_TRACKING_ID = process.env.MIX_NEXT_PUBLIC_GA_TRACKING_ID

    if (!GA_TRACKING_ID) {
        console.warn('Google Analytics Tracking ID not found.')
        return
    }

    const wasAlreadyLoaded = Boolean(window.gtag)

    const applyGAConfig = () => {
        window.dataLayer = window.dataLayer || []
        if (!window.gtag) {
            const gtag = function () {
                window.dataLayer.push(arguments)
            }
            window.gtag = gtag
            window.gtag('js', new Date())
        }
        // Always pass user_id/company_id from session so we don't overwrite blade config.
        // If gtag was already initialized (e.g. by blade), this is a re-config on the same
        // page load, so use `update: true` to merge identity without sending a duplicate page_view.
        const config = getGAUserConfig()
        window.gtag(
            'config',
            GA_TRACKING_ID,
            wasAlreadyLoaded ? { ...config, update: true } : config,
        )
    }

    // In production, blade may have already loaded gtag and set config; don't load script again
    if (window.gtag) {
        applyGAConfig()
        return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`
    script.onload = applyGAConfig
    document.head.appendChild(script)
}

i18n.use(english)

const areListsEqual = (list1, list2) => {
    if (list1.length !== list2.length) return false

    return list1.every((obj1, index) => {
        const obj2 = list2[index]
        return JSON.stringify(obj1) === JSON.stringify(obj2)
    })
}

const memoizedGetUserFeatureFlags = memoize(async (dispatch, getState) => {
    // Get user information and check that exists
    const currentUser = getState()?.AuthSlice?.user
    if (currentUser?.id) {
        const userId = currentUser.id
        const oldFeatureFlag = currentUser.feature_flags
        // Get the user feature flags and wait for result
        const response = await userFeatureFlags({ userId })
        const newFlags = response?.data?.featureFlags

        // Checks that the lists are different and if they are then it updates the flags stored
        const areEqual = areListsEqual(oldFeatureFlag, newFlags)
        if (!areEqual) {
            dispatch(AuthActions.storeUserFeatureFlags(newFlags))
        }
    }
})

// Executes the function every 30 minutes
setInterval(() => {
    memoizedGetUserFeatureFlags.cache.clear() //Clean cache to execute again and don't send unnesesary calls
    memoizedGetUserFeatureFlags(store.dispatch, store.getState)
}, 3000000) // 30 minutes

setupExpiredSessionInterceptor(store.dispatch, store.getState)

const getErrorMessage = error => error?.message ?? (error ? String(error) : '')

const isAxiosRejection = reason => {
    return Boolean(
        reason?.isAxiosError ||
        reason?.config?.url ||
        reason?.response?.config?.url ||
        reason?.request,
    )
}

const isChunkLoadError = reason => {
    const message = getErrorMessage(reason)
    return (
        reason?.name === 'ChunkLoadError' ||
        /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
            message,
        )
    )
}

const shouldShowModalForWindowError = event => {
    if (event?.target && event.target !== window) {
        return false
    }

    return Boolean(event?.error || event?.message)
}

const shouldShowModalForUnhandledRejection = reason => {
    if (isAxiosRejection(reason)) {
        return false
    }

    return isChunkLoadError(reason)
}

const App = () => {
    const [showGlobalErrorModal, setShowGlobalErrorModal] = useState(false)
    console.log('showGlobalErrorModal', showGlobalErrorModal)

    useEffect(() => {
        monitorOnlineOffline()
        initializeGA()
    }, [])

    useEffect(() => {
        const handleGlobalError = event => {
            if (!shouldShowModalForWindowError(event)) {
                return
            }

            notifyErrorModalShown(REASONS.WINDOW_ERROR, {
                message: event?.message,
                stack: event?.error?.stack,
                url: event?.filename,
            })
            setShowGlobalErrorModal(true)
        }

        const handleUnhandledRejection = event => {
            const reason = event?.reason
            if (!shouldShowModalForUnhandledRejection(reason)) {
                return
            }

            notifyErrorModalShown(REASONS.UNHANDLED_REJECTION, {
                message: getErrorMessage(reason),
                stack: reason?.stack,
                url: typeof window !== 'undefined' ? window.location.href : '',
            })
            setShowGlobalErrorModal(true)
        }

        window.addEventListener('error', handleGlobalError)
        window.addEventListener('unhandledrejection', handleUnhandledRejection)

        return () => {
            window.removeEventListener('error', handleGlobalError)
            window.removeEventListener(
                'unhandledrejection',
                handleUnhandledRejection,
            )
        }
    }, [])

    return (
        <BrowserCheck>
            <Provider store={store}>
                {/* <ErrorBoundary> */}
                <BrowserRouter>
                    <ScrollToTop>
                        <VersionCheck>
                            <FetchStateProvider>
                                <Routes />
                            </FetchStateProvider>
                        </VersionCheck>
                    </ScrollToTop>
                </BrowserRouter>
                {/* </ErrorBoundary> */}
                {/* {showGlobalErrorModal && (
                    <ErrorModal
                        isOpen={true}
                        onReload={reloadWithoutCache}
                        onGoHome={() => {
                            window.location.href = '/'
                        }}
                    />
                )} */}
            </Provider>
        </BrowserCheck>
    )
}

if (document.getElementById('app')) {
    createRoot(document.getElementById('app')).render(<App />)
}

export default App
